import { useCallback, useState } from "react";
import { toast } from "sonner";
import { uploadReviewPhoto } from "@/hooks/useReviews";
import { formatApiError } from "@/lib/api";
import { compressImage } from "@/lib/image";
import { logger } from "@/lib/logger";

export const MAX_REVIEW_PHOTOS = 3;

export function usePhotoUploads() {
  const [photos, setPhotos] = useState([]); // {key, preview, id, url, uploading}

  const addFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).slice(0, MAX_REVIEW_PHOTOS - photos.length);
    if (!files.length) return;
    const entries = files.map((file) => ({ key: `${file.name}-${Date.now()}-${Math.random()}`, file, preview: URL.createObjectURL(file), uploading: true }));
    setPhotos((p) => [...p, ...entries]);
    await Promise.all(
      entries.map(async (entry) => {
        try {
          const res = await uploadReviewPhoto(await compressImage(entry.file));
          setPhotos((p) => p.map((x) => (x.key === entry.key ? { ...x, id: res.id, url: res.url, uploading: false } : x)));
        } catch (err) {
          logger.warn("Review photo upload failed", err);
          toast.error(formatApiError(err.response?.data?.detail) || "Photo upload failed");
          setPhotos((p) => p.filter((x) => x.key !== entry.key));
        }
      })
    );
  }, [photos.length]);

  const removePhoto = useCallback((key) => setPhotos((p) => p.filter((x) => x.key !== key)), []);
  const reset = useCallback(() => setPhotos([]), []);

  return {
    photos,
    addFiles,
    removePhoto,
    reset,
    uploading: photos.some((p) => p.uploading),
    photoIds: photos.filter((p) => p.id).map((p) => p.id),
  };
}
